import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import locales from '../../../content.json';
import s from './index.module.scss';
import img from './img/img.png';
import { RootState } from '../../../store';

type AgeLabels = {
  current: string;
  first: string;
  second: string;
  third: string;
};

const getAgeKey = (rawAge: unknown): string | undefined => {
  if (!rawAge) return undefined;

  if (Array.isArray(rawAge)) {
    const code = rawAge[2];
    const label = rawAge[1];

    if (typeof code === 'string') {
      return code;
    }

    if (typeof label === 'string') {
      switch (label) {
        case '0-6 months':
          return '0-6m';
        case '7-12 months':
          return '7-12m';
        case '1-2 years':
          return '1-2y';
        case '2-7 years':
          return '2-7y';
        case 'More than 7 years':
          return 'more7y';
        default:
          return undefined;
      }
    }

    return undefined;
  }

  if (typeof rawAge === 'string') {
    switch (rawAge) {
      case '0-6m':
      case '7-12m':
      case '1-2y':
      case '2-7y':
      case 'more7y':
        return rawAge;

      case '0-6 months':
        return '0-6m';
      case '7-12 months':
        return '7-12m';
      case '1-2 years':
        return '1-2y';
      case '2-7 years':
        return '2-7y';
      case 'More than 7 years':
        return 'more7y';
      default:
        return undefined;
    }
  }

  return undefined;
};

const DogBehaviorProgramCard: FC = () => {
  const rawAge = useSelector((state: RootState) => state.data.age);
  const k9Funnel = useSelector((state: RootState) => state.data.k9Funnel);

  const lang =
    (typeof window !== 'undefined' && window.userLang && locales[window.userLang])
      ? window.userLang
      : 'en';

  const dict = locales[lang];

  const ageLabelsByKey: Record<string, AgeLabels> = useMemo(() => ({
    '0-6m': {
      current: dict.payment_text_234,
      first: dict.payment_text_235,
      second: dict.payment_text_236,
      third: dict.payment_text_237,
    },
    '7-12m': {
      current: dict.payment_text_234,
      first: dict.payment_text_235,
      second: dict.payment_text_236,
      third: dict.payment_text_237,
    },
    '1-2y': {
      current: dict.payment_text_238,
      first: dict.payment_text_239,
      second: dict.payment_text_240,
      third: dict.payment_text_237,
    },
    '2-7y': {
      current: dict.payment_text_241,
      first: dict.payment_text_242,
      second: dict.payment_text_243,
      third: dict.payment_text_244,
    },
    '1-5y': {
      current: dict.payment_text_241,
      first: dict.payment_text_242,
      second: dict.payment_text_243,
      third: dict.payment_text_244,
    },
    'more5y': {
      current: dict.payment_text_241,
      first: dict.payment_text_242,
      second: dict.payment_text_243,
      third: dict.payment_text_244,
    },
    'more7y': {
      current: dict.payment_text_241,
      first: dict.payment_text_242,
      second: dict.payment_text_243,
      third: dict.payment_text_244,
    },
  }), [dict]);

  const labels = useMemo(() => {
    const ageKey = getAgeKey(rawAge);

    if (!ageKey || !ageLabelsByKey[ageKey]) {
      return ageLabelsByKey['0-6m'];
    }

    return ageLabelsByKey[ageKey];
  }, [rawAge, ageLabelsByKey]);

  return (
    <div className={s.card} style={{ borderRadius: k9Funnel ? '16px' : null }}>
      <h3
        className={s.title}
        dangerouslySetInnerHTML={{ __html: dict.payment_text_230 }}
      />

      <div className={s.imageBox}>
        <img src={img} alt="" className={s.image} />

        <span className={`${s.label} ${s.labelCurrent}`}>
          {labels.current}
        </span>

        <span className={`${s.label} ${s.labelFirst}`}>
          {labels.first}
        </span>

        <span className={`${s.label} ${s.labelSecond}`}>
          {labels.second}
        </span>

        <span className={`${s.label} ${s.labelThird}`}>
          {labels.third}
        </span>

        <span className={`${s.label} ${s.labelCenter}`}>
          {dict.payment_text_231}
        </span>
      </div>

      <div className={s.legend}>
        <div className={s.legendItem}>
          <span className={`${s.dot} ${s.dotTraining}`} />
          <span>{dict.payment_text_232}</span>
        </div>

        <div className={s.legendItem}>
          <span className={`${s.dot} ${s.dotResults}`} />
          <span>{dict.payment_text_233}</span>
        </div>
      </div>
    </div>
  );
};

export default DogBehaviorProgramCard;